// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  formatCompactElapsedTime,
} from "../core/format.js";
import {
  cylinderNumberFromKey,
} from "../core/schema.js";
import {
  chtParameters,
} from "../analysis/detectors/cht.js";

export const HEALTH_METRIC_SCOPES = Object.freeze({
  ENGINE: "engine",
  AIRCRAFT: "aircraft",
  COMPARISON: "comparison",
  CONTEXT: "context",
});

const healthMetricScopeByKey = Object.freeze({
  sensors: HEALTH_METRIC_SCOPES.ENGINE,
  turbo: HEALTH_METRIC_SCOPES.ENGINE,
  oilSystem: HEALTH_METRIC_SCOPES.ENGINE,
  coolantMargin: HEALTH_METRIC_SCOPES.ENGINE,
  takeoffEnrichment: HEALTH_METRIC_SCOPES.ENGINE,
  cht: HEALTH_METRIC_SCOPES.ENGINE,
  chtRhythm: HEALTH_METRIC_SCOPES.CONTEXT,
  egt: HEALTH_METRIC_SCOPES.ENGINE,
  valve: HEALTH_METRIC_SCOPES.ENGINE,
  combustion: HEALTH_METRIC_SCOPES.CONTEXT,
  abnormalCombustion: HEALTH_METRIC_SCOPES.ENGINE,
  divergence: HEALTH_METRIC_SCOPES.CONTEXT,
  descentEgtDropout: HEALTH_METRIC_SCOPES.CONTEXT,
  inductionLeak: HEALTH_METRIC_SCOPES.ENGINE,
  mag: HEALTH_METRIC_SCOPES.CONTEXT,
  carbIce: HEALTH_METRIC_SCOPES.ENGINE,
  cooling: HEALTH_METRIC_SCOPES.ENGINE,
  coolingRate: HEALTH_METRIC_SCOPES.ENGINE,
  thermalContext: HEALTH_METRIC_SCOPES.CONTEXT,
  cylinderHarmony: HEALTH_METRIC_SCOPES.CONTEXT,
  cylinderStartupSymmetry: HEALTH_METRIC_SCOPES.CONTEXT,
  cylinderPersonality: HEALTH_METRIC_SCOPES.CONTEXT,
  powerChangeContext: HEALTH_METRIC_SCOPES.CONTEXT,
  ropLeaningContext: HEALTH_METRIC_SCOPES.CONTEXT,
  lopLeaningContext: HEALTH_METRIC_SCOPES.CONTEXT,
  fuelFlowResponse: HEALTH_METRIC_SCOPES.CONTEXT,
  fuelPressure: HEALTH_METRIC_SCOPES.CONTEXT,
  propellerRpm: HEALTH_METRIC_SCOPES.CONTEXT,
  fuelFlowWorkload: HEALTH_METRIC_SCOPES.CONTEXT,
  stablePowerQuality: HEALTH_METRIC_SCOPES.CONTEXT,
  flightCompleteness: HEALTH_METRIC_SCOPES.CONTEXT,
  sensorCoherence: HEALTH_METRIC_SCOPES.CONTEXT,
  phaseEvents: HEALTH_METRIC_SCOPES.CONTEXT,
  patternWorkContext: HEALTH_METRIC_SCOPES.CONTEXT,
  operationalNormalcy: HEALTH_METRIC_SCOPES.CONTEXT,
  charging: HEALTH_METRIC_SCOPES.AIRCRAFT,
  electrical: HEALTH_METRIC_SCOPES.AIRCRAFT,
  comparison: HEALTH_METRIC_SCOPES.COMPARISON,
  engineDelta: HEALTH_METRIC_SCOPES.COMPARISON,
  profile: HEALTH_METRIC_SCOPES.CONTEXT,
  rawData: HEALTH_METRIC_SCOPES.CONTEXT,
  instrumentationSuspicion: HEALTH_METRIC_SCOPES.AIRCRAFT,
});

const healthMetricScopeLabels = Object.freeze({
  [HEALTH_METRIC_SCOPES.ENGINE]: "engine-specific",
  [HEALTH_METRIC_SCOPES.AIRCRAFT]: "aircraft context",
  [HEALTH_METRIC_SCOPES.COMPARISON]: "comparison context",
  [HEALTH_METRIC_SCOPES.CONTEXT]: "supporting context",
});

export function normalizeHealthMetricScope(scope) {
  return Object.values(HEALTH_METRIC_SCOPES).includes(scope)
    ? scope
    : HEALTH_METRIC_SCOPES.CONTEXT;
}

export function healthMetricScopeForKey(key) {
  return healthMetricScopeByKey[key] ?? HEALTH_METRIC_SCOPES.CONTEXT;
}

export function metricHasScoredFinding(key, metric) {
  if (!Number.isFinite(metric?.score)) return false;
  if (key === "mag") return metric.score < 88;
  if (key === "charging") {
    const status = metric.details?.status;
    return status === "warning" || status === "fail" || metric.score < 88;
  }
  return true;
}

export function healthMetricScopeForMetric(key, metric) {
  if (key === "mag") {
    return metricHasScoredFinding(key, metric)
      ? HEALTH_METRIC_SCOPES.ENGINE
      : HEALTH_METRIC_SCOPES.CONTEXT;
  }
  if (key === "charging") {
    return metricHasScoredFinding(key, metric)
      ? HEALTH_METRIC_SCOPES.AIRCRAFT
      : HEALTH_METRIC_SCOPES.CONTEXT;
  }
  return healthMetricScopeForKey(key);
}

export function healthMetricScopeLabel(scope) {
  return healthMetricScopeLabels[normalizeHealthMetricScope(scope)];
}

export function metricParticipatesInScopedScore(key, metric, scoringScope = HEALTH_METRIC_SCOPES.AIRCRAFT) {
  if (!Number.isFinite(metric?.score) || metric.details?.status === "not-applicable") return false;
  const metricScope = healthMetricScopeForMetric(key, metric);
  const normalizedScoringScope = normalizeHealthMetricScope(scoringScope);
  if (normalizedScoringScope === HEALTH_METRIC_SCOPES.ENGINE) return metricScope === HEALTH_METRIC_SCOPES.ENGINE;
  if (normalizedScoringScope === HEALTH_METRIC_SCOPES.COMPARISON) return metricScope === HEALTH_METRIC_SCOPES.COMPARISON;
  if (normalizedScoringScope === HEALTH_METRIC_SCOPES.CONTEXT) return metricScope === HEALTH_METRIC_SCOPES.CONTEXT;
  return metricScope !== HEALTH_METRIC_SCOPES.CONTEXT;
}

function defaultScoreCardState(className) {
  return {
    red: "danger",
    amber: "warning",
    green: "good",
    muted: "muted",
  }[className] ?? "muted";
}

function contextWithDefaults(context = {}) {
  return {
    detectionWindowsForHealthKey: () => [],
    engineDescriptors: () => [],
    engineSourceNumbers: (engine) => (engine?.cylinders ?? [])
      .map((cylinder) => cylinder.sourceNumber ?? cylinder.number)
      .filter(Number.isFinite),
    findingDisplayClass: () => "muted",
    findingForMetric: (_key, summary) => summary?.finding ?? null,
    hasCarbTempChannel: () => true,
    healthMetricScope: healthMetricScopeForMetric,
    reviewMonitorEnabled: () => true,
    scoreCardStateFromClass: defaultScoreCardState,
    ...context,
  };
}

export function engineCheckDefinitions() {
  return [
    ["sensors", "Sensors", "Probe trace integrity"],
    ["engineDelta", "Engine Delta", "Cross-engine comparison"],
    ["turbo", "Turbo", "Boost, TIT, and turbo heat context"],
    ["oilSystem", "Oil System", "Oil temperature and pressure"],
    ["coolantMargin", "Coolant Margin", "Coolant temperature margin"],
    ["takeoffEnrichment", "Takeoff Enrichment", "Full-power fuel-flow adequacy"],
    ["cht", "CHT Heat", "Cylinder-head temperature"],
    ["chtRhythm", "CHT Rhythm", "Repeating CHT-only movement"],
    ["egt", "EGT Balance", "Exhaust gas spread"],
    ["valve", "EGT Rhythm", "Stable-window wobble"],
    ["combustion", "CHT/EGT Pairing", "Paired CHT/EGT movement"],
    ["abnormalCombustion", "Abnormal Combustion", "Single-cylinder CHT runaway with flat EGT"],
    ["divergence", "Transient divergence", "Cylinders fan apart and reconverge"],
    ["descentEgtDropout", "Descent EGT Dropout", "One EGT cools sharply in descent"],
    ["inductionLeak", "Induction Leak", "Cylinder EGT response during power reduction"],
    ["mag", "Mag Check", "Ignition-test EGT response"],
    ["carbIce", "Carb Ice", "Carb-temp/OAT power-loss cue"],
    ["cooling", "Cooling", "CHT versus power and airflow"],
    ["coolingRate", "Cooling Rate", "CHT drop rate versus configured trigger"],
    ["charging", "Start Charge", "Post-start voltage/current recovery"],
    ["electrical", "Electrical", "Bus voltage behavior"],
    ["instrumentationSuspicion", "Probe Reference", "Shared probe reference anomalies"],
  ];
}

// The exact `note` a detector emits when an aircraft's logs physically lack the
// sensor channel a monitor needs (structural unavailability), as opposed to a
// transient "not this flight" null ("Need more samples", "No engine start
// detected") which means the channel IS present, just idle. Turbo is handled
// separately via details.available. inductionLeak is intentionally absent: it
// has a fuel+EGT fallback path, so it is never structurally unavailable.
// takeoffEnrichment is also intentionally absent: it is opt-in (config-gated on
// a per-engine target), not purely channel-gated, so its unavailable states are
// not surfaced through this map in the current phase.
// Pinned to live detector output by the contract test in
// tests/engine_review_model_check.mjs.
export const MONITOR_CHANNEL_UNAVAILABLE_NOTES = Object.freeze({
  sensors: ["No sensor channels"],
  oilSystem: ["No oil temperature or pressure channels"],
  coolantMargin: ["No coolant temperature channel"],
  cht: ["No CHT channel data"],
  coolingRate: ["No CHT channel data"],
  chtRhythm: ["Need multiple CHT channels"],
  cooling: ["Need multiple CHT channels", "No comparable CHT channels"],
  egt: ["No EGT spread data"],
  valve: ["Need multiple EGT channels"],
  divergence: ["Need multiple EGT or CHT channels"],
  descentEgtDropout: ["Need multiple EGT channels"],
  combustion: ["Need paired CHT and EGT channels"],
  abnormalCombustion: ["Need per-cylinder CHT and EGT channels"],
  mag: ["Need RPM channel for mag-check screen"],
  carbIce: ["No carb temperature channel", "No OAT channel for carb-ice screen"],
  electrical: ["No bus voltage channel"],
  charging: ["No bus voltage channel"],
});

const MONITOR_CHANNEL_UNAVAILABLE_REASONS = Object.freeze({
  sensors: "No probe channels in this aircraft's logs",
  oilSystem: "No oil temperature or pressure channel in this aircraft's logs",
  coolantMargin: "No coolant-temperature channel in this aircraft's logs",
  cht: "No CHT channel in this aircraft's logs",
  coolingRate: "No CHT channel in this aircraft's logs",
  chtRhythm: "Needs multiple CHT channels, not present in this aircraft's logs",
  cooling: "Needs multiple CHT channels, not present in this aircraft's logs",
  egt: "No EGT channel in this aircraft's logs",
  valve: "Needs multiple EGT channels, not present in this aircraft's logs",
  divergence: "Needs multiple EGT or CHT channels, not present in this aircraft's logs",
  descentEgtDropout: "Needs multiple EGT channels, not present in this aircraft's logs",
  combustion: "Needs paired CHT and EGT channels, not present in this aircraft's logs",
  abnormalCombustion: "Needs per-cylinder CHT and EGT channels, not present in this aircraft's logs",
  mag: "No RPM channel in this aircraft's logs",
  carbIce: "No carb-temperature/OAT channel pair in this aircraft's logs",
  electrical: "No bus-voltage channel in this aircraft's logs",
  charging: "No bus-voltage channel in this aircraft's logs",
  turbo: "No turbocharger telemetry in this aircraft's logs",
});

export function monitorChannelUnavailableReason(key) {
  return MONITOR_CHANNEL_UNAVAILABLE_REASONS[key] ?? "Required channel not present in this aircraft's logs";
}

// One flight's verdict for a monitor: can this aircraft's instrumentation feed
// the channel the monitor needs? "available" (it scored, or the channel is
// present), "unavailable" (a structural no-channel null), or "unknown" (no
// metric, or a null we can't confidently attribute to a missing channel — e.g.
// a context detector that simply did not trigger this flight).
export function metricChannelAvailability(key, metric) {
  if (!metric || typeof metric !== "object") return "unknown";
  if (Number.isFinite(metric.score)) return "available";
  // Turbo carries an explicit availability flag: it is false only when no turbo
  // equipment is configured or inferred. A configured turbo missing telemetry
  // on one flight still reports available:true, so this never false-flags.
  if (key === "turbo") return metric.details?.available === false ? "unavailable" : "available";
  const structuralNotes = MONITOR_CHANNEL_UNAVAILABLE_NOTES[key];
  if (structuralNotes && structuralNotes.includes(String(metric.note ?? ""))) return "unavailable";
  return "unknown";
}

export function engineForSummaryScope(summary, context = {}) {
  const { engineDescriptors } = contextWithDefaults(context);
  const engineId = summary?.scope?.engineId;
  if (!engineId) return null;
  return engineDescriptors().find((engine) => engine.id === engineId || engine.side === engineId) ?? null;
}

function engineSourceNumberSet(engine, context = {}) {
  const { engineSourceNumbers } = contextWithDefaults(context);
  return new Set(engineSourceNumbers(engine));
}

function metricProbeKey(key, metric) {
  if (key === "egt" && !metric?.details?.hot?.isReview && metric?.details?.sustainedEgtSeparation) {
    return (metric.details.sustainedLowEgt ?? metric.details.sustainedEgtSeparation).key;
  }
  if (["cht", "abnormalCombustion"].includes(key) && metric?.details?.severityFloor) return metric.details.severityFloor.channelKey;
  if (key === "sensors") return metric?.details?.top?.key ?? "";
  if (key === "egt") return metric?.details?.dominant?.key ?? "";
  if (key === "inductionLeak") return metric?.details?.top?.key ?? "";
  if (key === "cht" && Number.isFinite(metric?.details?.hottest?.cylinder)) return `cht${metric.details.hottest.cylinder}`;
  if (key === "chtRhythm" && Number.isFinite(metric?.details?.top?.cylinder)) return `cht${metric.details.top.cylinder}`;
  if (key === "coolingRate") return metric?.details?.top?.key ?? "";
  return "";
}

export function engineMetricCylinder(key, metric) {
  if (key === "egt" && !metric?.details?.hot?.isReview && metric?.details?.sustainedEgtSeparation) {
    return (metric.details.sustainedLowEgt ?? metric.details.sustainedEgtSeparation).cylinder;
  }
  if (["cht", "abnormalCombustion"].includes(key) && metric?.details?.severityFloor) return metric.details.severityFloor.sourceCylinder;
  const top = metric?.details?.top ?? {};
  if (Number.isFinite(top.cylinder)) return top.cylinder;
  if (Array.isArray(top.cylinders) && Number.isFinite(top.cylinders[0])) return top.cylinders[0];
  if (key === "cht" && Number.isFinite(metric?.details?.hottest?.cylinder)) return metric.details.hottest.cylinder;
  if (key === "chtRhythm" && Number.isFinite(metric?.details?.top?.cylinder)) return metric.details.top.cylinder;
  if (key === "egt" && Number.isFinite(metric?.details?.dominant?.cylinder)) return metric.details.dominant.cylinder;
  if (key === "mag" && Number.isFinite(top.weakResponses?.[0]?.cylinder)) return top.weakResponses[0].cylinder;
  if (key === "sensors") return cylinderNumberFromKey(top.key);
  return null;
}

export function metricMatchesEngineScope(key, metric, engine, context = {}) {
  if (!engine) return true;
  const sourceNumbers = engineSourceNumberSet(engine, context);
  const cylinder = engineMetricCylinder(key, metric);
  if (Number.isFinite(cylinder)) return sourceNumbers.has(cylinder);
  const probeKey = metricProbeKey(key, metric);
  const probeCylinder = cylinderNumberFromKey(probeKey);
  if (Number.isFinite(probeCylinder)) return sourceNumbers.has(probeCylinder);
  return true;
}

function engineForSourceNumber(sourceNumber, context = {}) {
  if (!Number.isFinite(sourceNumber)) return null;
  const { engineDescriptors, engineSourceNumbers } = contextWithDefaults(context);
  return engineDescriptors().find((engine) => new Set(engineSourceNumbers(engine)).has(sourceNumber)) ?? null;
}

export function engineWindowIdentity(key, metric, window = {}, summary = {}, context = {}) {
  const fullContext = contextWithDefaults(context);
  const scope = normalizeHealthMetricScope(window.scope ?? fullContext.healthMetricScope(key));
  const channelKey = (window.channelKey ?? metricProbeKey(key, metric)) || undefined;
  const sourceCylinder = Number.isFinite(window.sourceCylinder)
    ? window.sourceCylinder
    : Number.isFinite(window.cylinder)
      ? window.cylinder
      : cylinderNumberFromKey(channelKey) ?? engineMetricCylinder(key, metric);
  const summaryEngine = engineForSummaryScope(summary, fullContext);
  const cylinderEngine = engineForSourceNumber(sourceCylinder, fullContext);
  const scopedEngine = scope === HEALTH_METRIC_SCOPES.ENGINE
    ? cylinderEngine ?? summaryEngine
    : null;
  return {
    scope,
    channelKey,
    sourceCylinder: Number.isFinite(sourceCylinder) ? sourceCylinder : undefined,
    engineId: window.engineId ?? scopedEngine?.id,
    engineSide: window.engineSide ?? scopedEngine?.side,
  };
}

export function annotateEngineWindow(key, metric, window = {}, summary = {}, context = {}) {
  const identity = engineWindowIdentity(key, metric, window, summary, context);
  return {
    ...window,
    scope: identity.scope,
    ...(identity.channelKey ? { channelKey: identity.channelKey } : {}),
    ...(Number.isFinite(identity.sourceCylinder) ? { sourceCylinder: identity.sourceCylinder } : {}),
    ...(identity.engineId ? { engineId: identity.engineId } : {}),
    ...(identity.engineSide ? { engineSide: identity.engineSide } : {}),
  };
}

export function engineMetricRows(summary, context = {}) {
  const fullContext = contextWithDefaults(context);
  const scopeEngine = engineForSummaryScope(summary, fullContext);
  const engineCount = fullContext.engineDescriptors().length;
  return engineCheckDefinitions().map(([key, label, description]) => {
    const metric = summary[key] ?? {};
    const scope = normalizeHealthMetricScope(fullContext.healthMetricScope(key, metric));
    const monitorEnabled = fullContext.reviewMonitorEnabled(key);
    if (key === "turbo" && !metric.details?.available) return null;
    if (key === "carbIce" && !fullContext.hasCarbTempChannel(summary)) return null;
    if (scope === HEALTH_METRIC_SCOPES.COMPARISON && engineCount <= 1) return null;
    if (scopeEngine && scope === HEALTH_METRIC_SCOPES.ENGINE && !metricMatchesEngineScope(key, metric, scopeEngine, fullContext)) return null;
    const finding = fullContext.findingForMetric(key, summary);
    const isContext = scope === HEALTH_METRIC_SCOPES.CONTEXT || Boolean(scopeEngine && scope !== HEALTH_METRIC_SCOPES.ENGINE);
    const isMonitorDisabled = !monitorEnabled;
    const scopeLabel = healthMetricScopeLabel(scope);
    const className = isContext || isMonitorDisabled ? "muted" : fullContext.findingDisplayClass(finding, metric);
    return {
      key,
      label,
      description: isMonitorDisabled ? `${description} - muted for this aircraft` : isContext ? `${description} - ${scopeLabel}` : description,
      scope,
      scopeLabel,
      isContext,
      isMonitorDisabled,
      metric,
      finding,
      className,
      state: fullContext.scoreCardStateFromClass(className),
    };
  }).filter(Boolean);
}

export function engineSeverityRank(className) {
  return { red: 0, amber: 1, green: 2, muted: 3 }[className] ?? 3;
}

export function engineReviewItems(summary, context = {}) {
  const fullContext = contextWithDefaults(context);
  const items = engineMetricRows(summary, fullContext)
    .filter((row) => !row.isContext && !row.isMonitorDisabled && Number.isFinite(row.metric?.score) && row.metric.score < 88)
    .map((row) => ({
      ...row,
      windows: fullContext.detectionWindowsForHealthKey(row.key, row.metric)
        .map((window) => annotateEngineWindow(row.key, row.metric, window, summary, fullContext)),
      cylinder: engineMetricCylinder(row.key, row.metric),
    }))
    .sort((a, b) => engineSeverityRank(a.className) - engineSeverityRank(b.className) || (a.metric.score ?? 100) - (b.metric.score ?? 100));

  const chtPeakF = summary?.cht?.details?.max;
  if (Number.isFinite(chtPeakF) && chtPeakF >= chtParameters.peakReviewThresholdF) {
    const chtIndex = items.findIndex((item) => item.key === "cht");
    if (chtIndex > 0) {
      const [chtItem] = items.splice(chtIndex, 1);
      items.unshift(chtItem);
    }
  }

  return items;
}

export function noDataSummaryFinding(label = "This log") {
  return {
    title: "Not enough data for an overall review",
    meta: "No evaluated checks",
    body: `${label} does not have enough usable data in the enabled checks to produce an overall score. Unavailable checks do not establish normal engine behavior.`,
    causes: [],
    actions: [["low", "Review the available channels and enabled checks, then compare a log with suitable operating data"]],
    confidence: "Low",
  };
}

export function scopedSummaryFinding(summary, context = {}) {
  const fullContext = contextWithDefaults(context);
  const scopeEngine = engineForSummaryScope(summary, fullContext);
  if (!Number.isFinite(summary?.overall?.score)) return noDataSummaryFinding(scopeEngine?.label);
  if (!scopeEngine) return summary?.finding ?? null;
  const rows = engineReviewItems(summary, fullContext);
  if (rows.length) return rows[0].finding;
  return {
    title: `${scopeEngine.label} review clear`,
    meta: "No active scoped review cue",
    body: `${scopeEngine.label} does not have an active engine-specific cue at this sensitivity setting. Use aircraft or compare view for cross-engine context.`,
    causes: [["No scoped engine-specific review cue is active", 1]],
    actions: [["low", `Review ${scopeEngine.label} traces alongside aircraft and compare views if needed`]],
    confidence: "Low",
  };
}

function compactList(items) {
  const clean = items.map((item) => String(item ?? "").trim()).filter(Boolean);
  if (!clean.length) return "";
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
  return `${clean.slice(0, -1).join(", ")}, and ${clean.at(-1)}`;
}


export function engineMetricLesson(key, metric = {}, finding = {}) {
  const cylinder = engineMetricCylinder(key, metric);
  const scopedCylinder = String(finding?.title ?? "").match(/\b((?:Left|Right|Front|Rear|F|L|R)\s+Cyl(?:inder)?\s*#?\s*\d+|cylinder\s*#?\s*\d+)\b/i)?.[1];
  const cylinderLabel = scopedCylinder
    ? scopedCylinder.replace(/^cylinder/i, "Cylinder")
    : Number.isFinite(cylinder) ? `Cylinder ${cylinder}` : "";
  const cylinderText = cylinderLabel ? ` ${cylinderLabel} is the main cylinder called out in this log.` : "";
  const lessons = {
    overall: {
      title: "How to read the engine page",
      watches: "The page starts with the strongest advisory cue, then keeps the underlying system checks and cylinder comparison close by.",
      meaning: "A low score is a reason to inspect evidence, not a maintenance diagnosis. The useful question is whether the cue has a clear operating explanation, a data-quality explanation, or a repeating aircraft pattern.",
      review: "Start with the marked window, then compare the raw traces, operating phase, and nearby flights before deciding whether this is operational, data-quality, or maintenance context.",
    },
    sensors: {
      title: "Probe and sensor-integrity detection",
      watches: "This check looks for dropouts, impossible jumps, flatlines, out-of-range values, and probe behavior that does not agree with the other cylinders.",
      meaning: "Bad sensor data can look exactly like an engine problem. A weak EGT or CHT probe can create false spread, rhythm, cooling, or pairing cues downstream.",
      review: "Open the raw trace first. Look for steps, dropouts, flat spots, loose-probe behavior, and whether the paired CHT or EGT channel tells the same story.",
    },
    engineDelta: {
      title: "Cross-engine comparison",
      watches: "This compares the engines side by side in matched flight context: CHT, fuel flow at similar power, RPM, MAP, oil, TIT when available, and engine-side data coverage.",
      meaning: "A side-to-side split is a comparison cue. It can come from pilot technique, mixture, cowl flap position, synchronization, sensor calibration, installation differences, or a repeating engine-specific pattern.",
      review: "Start with matched RPM, MAP, fuel flow, CHT, oil, and phase. Only narrow the review to one engine when the same split repeats or the supporting traces point to one side.",
    },
    cht: {
      title: "Cylinder-head temperature",
      watches: "CHT is the heat retained by the cylinder head. It moves slower than EGT and is strongly affected by power, mixture, cooling airflow, baffling, and climb speed.",
      meaning: "Sustained high CHT or one cylinder running much warmer than peers can matter because it reflects the thermal load on metal parts, not just exhaust temperature.",
      review: `Check the flight phase, climb speed, power, mixture, and cooling-airflow setup before comparing cylinders. Repeated cruise separation deserves more attention than a one-off climb peak.${cylinderText}`,
    },
    egt: {
      title: "EGT balance",
      watches: "EGT spread compares exhaust temperatures between cylinders. Absolute EGT is less important than how one cylinder moves relative to the others.",
      meaning: "EGT separation can come from mixture distribution, ignition behavior, probe placement, or a normal power or mixture transition. It needs context before it means anything mechanical.",
      review: "Review EGT alongside fuel flow, RPM, MAP, and mixture changes. Then check whether the same cylinder separates in similar phases on nearby flights.",
    },
    valve: {
      title: "EGT rhythm detection",
      watches: "This check looks for repeating EGT oscillation during stable-power windows, weighing both the swing size and the frequency when one cylinder wobbles more than its peers.",
      meaning: "A repeatable rhythm with meaningful EGT movement can be an early review cue for a possible exhaust-valve anomaly, including a valve beginning to fail, but ignition, mixture movement, and probe noise can create similar shapes.",
      review: `Look for a clean, repeating same-cylinder rhythm during stable power. Compare against nearby flights and maintenance notes before connecting it to exhaust-valve behavior.${cylinderText}`,
    },
    combustion: {
      title: "CHT/EGT pairing detection",
      watches: "EGT reacts quickly to combustion and exhaust changes; CHT reacts more slowly to heat absorbed by the cylinder head. This check looks at how those two traces move together.",
      meaning: "Opposing EGT and CHT movement, or one cylinder moving differently from peers, can point toward ignition, mixture distribution, injector, plug, lead, or probe-context review.",
      review: `Keep power and mixture context visible, then compare the same cylinder's CHT and EGT movement. Mag-check notes, roughness notes, or repeat flights are the useful tie-breakers.${cylinderText}`,
    },
    abnormalCombustion: {
      title: "Abnormal-combustion detection",
      watches: "At high power this looks for one cylinder's CHT climbing fast while its EGT stays flat or falls and it pulls away from the other cylinders - heat going into the metal that the exhaust temperature does not explain.",
      meaning: "That decoupling is the classic fingerprint of detonation or pre-ignition, which can damage a piston or cylinder quickly. A noisy CHT probe can fake the same shape, so a suspect probe on that cylinder caps the cue at a watch instead of calling it red.",
      review: `Before more high-power running, have ignition timing, the spark plugs, the fuel injector and induction for that cylinder, and the cylinder's CHT probe checked. Compare against nearby flights to see whether the same cylinder repeats.${cylinderText}`,
    },
    divergence: {
      title: "Transient-divergence detection",
      watches: "This looks for cylinders fanning apart and then partly reconverging: EGT spread and CHT spread change together over a short event window.",
      meaning: "Divergence often means the cylinders did not respond equally to the same operating moment. That could be power, mixture, airflow, ignition, cooling, or sensor response.",
      review: "Overlay fuel flow, RPM, MAP, airspeed, altitude, and OAT around each highlighted event window. If those were moving, treat the split as operating-context first.",
    },
    descentEgtDropout: {
      title: "Descent EGT-dropout detection",
      watches: "This looks for one EGT trace dropping sharply below the peer group during descent or a low-power transition, using fuel flow, MAP, RPM, phase, and altitude as context.",
      meaning: "A descent dropout is a review cue, not a diagnosis. It can be operational combustion behavior at low power, uneven mixture distribution from a partially closed carb or throttle-body butterfly, an injector/nozzle issue, an induction or exhaust leak, or probe/wiring behavior.",
      review: `Compare the marked window against fuel flow, MAP, RPM, altitude, and pilot technique. The key question is whether the same cylinder repeats in similar descents.${cylinderText}`,
    },
    inductionLeak: {
      title: "Induction-leak cue detection",
      watches: "This compares cylinder EGT changes during power reductions, including differences between settled higher- and lower-MAP periods. A cylinder that drops less than its peers can qualify even with steady RPM.",
      meaning: "This is a passive review cue. Unknown mixture settings, ignition, fuel distribution, probe behavior, and operating changes can produce similar responses; the pattern does not establish an induction leak.",
      review: `Use the marked window with RPM, MAP, fuel flow, EGT, altitude, and pilot notes visible. Repeatability at similar operating conditions matters before treating it as an induction leak.${cylinderText}`,
    },
    mag: {
      title: "Ignition-test response detection",
      watches: "During a mag check, RPM normally drops and EGT often rises because combustion timing and flame travel change when one ignition source is removed.",
      meaning: "A weak or different EGT response on one cylinder can point toward plug, lead, or ignition-side review, but only if the app has found a real mag-check window.",
      review: `Confirm the mag-check window and switch direction from pilot notes or annotations. Without that context, use the trace as candidate evidence only.${cylinderText}`,
    },
    carbIce: {
      title: "Carb-ice cue detection",
      watches: "This check only runs when a carb-temperature channel is logged. It looks for carb temperature in an icing-prone range, tempered by OAT when available, with a whole-engine RPM/MAP decay, or a carb-temperature rise followed by RPM/MAP recovery.",
      meaning: "A carb-temperature channel strongly suggests the engine/monitor setup can support carb-ice screening, but the log still does not know carb-heat position or pilot intent.",
      review: "Use the marked window with carb temp, OAT, RPM, MAP, fuel flow, altitude, airspeed, and pilot notes visible. Treat it as possible carb ice only when the pattern fits the operating context.",
    },
    cooling: {
      title: "Cooling-airflow detection",
      watches: "This check looks for a cylinder whose CHT is hotter than the others without a matching EGT reason, especially when power and airflow context are available.",
      meaning: "That shape points more toward heat rejection and airflow than combustion. Baffles, seals, cowl flaps, climb speed, cowling fit, or probe placement can all matter.",
      review: `Compare the marked window with climb speed, cruise speed, power, and cowling/cowl-flap context. If the same cylinder repeats, inspect baffles, seals, and probe placement.${cylinderText}`,
    },
    coolingRate: {
      title: "CHT cooling-rate detection",
      watches: "This check scans CHT channels during engine-run phases and flags windows where CHT drops faster than the configured F/min trigger.",
      meaning: "A rapid CHT drop is usually operational context: power reduction, high cooling airflow, descent planning, or a brief trace issue. It is separate from the cooling-airflow card, which looks for one cylinder running hot.",
      review: `Review the descent or power-reduction window with RPM, MAP, fuel flow, and airspeed visible. If only one CHT channel steps down, check for probe behavior before changing operations.${cylinderText}`,
    },
    coolantMargin: {
      title: "Coolant-temperature margin detection",
      watches: "This check watches the coolant-temperature channel during running-engine phases and flags sustained operation near or above the current Rotax-style high-temperature limit.",
      meaning: "Coolant margin is separate from oil temperature. Sustained high coolant temperature can be climb speed, ambient temperature, radiator airflow, coolant level, thermostat behavior, installation cooling margin, or monitor scaling.",
      review: "Use the marked coolant window with RPM, MAP, fuel flow, oil temperature, airspeed, and OAT visible. Confirm the aircraft's published coolant limit and the monitor scaling before treating it as a mechanical issue.",
    },
    takeoffEnrichment: {
      title: "Takeoff fuel-flow enrichment detection",
      watches: "This check only runs when you configure a full-power fuel-flow target (or rated horsepower) for the engine and the log has percent power or manifold pressure. It compares full-power fuel flow against that target, derated for the power actually available.",
      meaning: "At full power the mixture is deliberately rich; the extra fuel cools the cylinders and protects against detonation. A persistent shortfall points more toward fuel-system rigging, a clogged injector or distributor, or a weak fuel pump than toward pilot technique — though leaning on takeoff produces the same shape.",
      review: "Confirm the mixture was full rich, then have an A&P check fuel flow at full (takeoff) power against the manufacturer's target. Verify the configured target and the fuel-flow channel scaling before treating it as mechanical.",
    },
    charging: {
      title: "Start/charging recovery detection",
      watches: "This check separates cranking sag from post-start recovery. A healthy start usually shows voltage sag during cranking, then bus voltage returning to charging range.",
      meaning: "Low crank voltage points more toward battery, starter load, cables, grounds, or contactors. Poor recovery after start points more toward alternator, field, regulator, belt, or wiring.",
      review: "Check bus voltage before crank, during crank, and after start. If amps are logged, confirm whether the system actually recharged after the start event.",
    },
    electrical: {
      title: "Running bus-voltage detection",
      watches: "This check looks at the electrical bus after excluding start and shutdown. It is watching for voltage that is too low, too high, or unstable while the engine is running.",
      meaning: "Running voltage problems can be alternator/regulator behavior, load changes, wiring or grounding resistance, sensor scaling, or phase-classification noise.",
      review: "Compare voltage against RPM, amps, and known electrical loads such as lights or pitot heat. Separate running-voltage issues from start-only battery sag.",
    },
  };
  return lessons[key] ?? lessons.overall;
}

export function engineFindingSnippet(value, maxLength = 190) {
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  const sentence = text.match(/^(.+?[.!?])(?:\s|$)/)?.[1] ?? text;
  if (sentence.length <= maxLength) return sentence;
  return `${sentence.slice(0, Math.max(0, maxLength - 1)).trim()}...`;
}

export function engineWindowLabel(windows) {
  const visible = windows.filter((window) => Number.isFinite(window.startTime));
  if (!visible.length) return "";
  if (visible.length > 1) return `${visible.length} marked windows`;
  const window = visible[0];
  return `${formatCompactElapsedTime(window.startTime)}-${formatCompactElapsedTime(window.endTime)}`;
}

export function engineReviewActionList(reviewItems, fallbackFinding) {
  const priorityRank = { high: 0, medium: 1, low: 2 };
  const sourceItems = reviewItems.length ? reviewItems : [{ label: "Engine", finding: fallbackFinding }];
  const seen = new Set();
  return sourceItems.flatMap((item) => (item.finding?.actions ?? []).map(([priority, text]) => ({
    priority,
    text,
    source: item.label,
  })))
    .sort((a, b) => (priorityRank[a.priority] ?? 9) - (priorityRank[b.priority] ?? 9))
    .filter((item) => {
      if (!item.text || seen.has(item.text)) return false;
      seen.add(item.text);
      return true;
    })
    .slice(0, 3);
}

export function engineTriageStatus(reviewItems, summary) {
  const redItems = reviewItems.filter((item) => item.className === "red");
  if (redItems.length) {
    return {
      label: redItems.every((item) => item.key === "valve") ? "Warning" : "Attention",
      className: "red",
    };
  }
  if (reviewItems.some((item) => item.className === "amber")) return { label: "Watch", className: "amber" };
  return { label: summary?.overall?.label ?? "No data", className: summary?.overall?.className ?? "muted" };
}
